import { View, CollectionView } from "backbone.marionette";
import { Backbone } from "extensions/backbone";

import { _ } from "extensions/underscore";

import "./view.scss";

const CollectionViewClassOptions = [
    ["childView", false],
    ["childViewEventPrefix", false],
    ["childViewEvents", false],
    ["childViewOptions", true],
    ["emptyView", false],
    ["emptyViewOptions", false],
    ["reorderOnSort", false],
    ["viewComparator", true]
];

const ListView = CollectionView.extend({
    className: "inner-scroll-container",
    
    constructor: function constructor() {
        CollectionView.prototype.constructor.apply(this, arguments);
    
        // Merge parent options into this collection
        const parent = this.getOption("_parent");
        if (!parent.options) {
            return;
        }
    
        CollectionViewClassOptions.forEach((key) => {
            this[key[0]] = parent.getOption(key[0]);
            if (key[1] && typeof this[key[0]] === "function") {
                this[key[0]] = this[key[0]].bind(parent);
            }
        });
    }
});

export const VirtualizedListView = View.extend({
    className: "virtualized",

    itemHeight: 26,

    events: {
        scroll: "virtualScroll"
    },

    template: () => "<div class='inner-scroll-container'></div>",

    regions: {
        scrollable: {
            el: ".inner-scroll-container",
            replaceElement: true
        }
    },

    // How many items can we render?
    getViewsCount() {
        return Math.floor(this.getListHeight() / this.getOption("itemHeight"));
    },

    virtualScroll() {
        const start = Math.max(0, Math.floor(this.el.scrollTop / this.getOption("itemHeight")) - 3);
        const count = this.getViewsCount() + 6;
        const end = Math.min(this.collection.length, start + count);

        this.virtualHideItems({ start, end, count });
        this.virtualShowItems({ start, end, count });
    },

    getFirstRenderedIndex() {
        return this._allViews.findIndex(Boolean);
    },

    virtualHideItems({ start, end, count }) {
        const firstRenderedIndex = this.getFirstRenderedIndex();
        let _startHidingIndex;
        let _endHidingIndex;

        if (firstRenderedIndex <= start) {
            // Scrolling down
            _startHidingIndex = firstRenderedIndex;
            _endHidingIndex = start;
        } else {
            // Scrolling up
            _startHidingIndex = start + count;
            _endHidingIndex = firstRenderedIndex + count;
        }

        for (let index = _startHidingIndex; index < _endHidingIndex; index++) {
            const viewToRemove = this.list.children.find((child) => child.model.collection.indexOf(child.model) === index);
            if (viewToRemove) {
                this.list.removeChildView(viewToRemove);
                this._allViews[index] = false;
            }
        }
    },

    virtualShowItems({ start, end, count }) {
        for (let index = start; index < end; index++) {
            if (this._allViews[index] === false) {
                const view = this.list._createChildView(this.collection.models[index]);
                this.list.addChildView(view, index);
                view.el.style.top = `${index * this.getOption("itemHeight")}px`;
                view.el.dataset.virt_index = index;
                this._allViews[index] = true;
            }
        }
    },
    
    getListHeight() {
        // Get max-height from css
        const maxHeight = Number.parseInt(this.$el.css("max-height"), 10);
        const parentHeight = this.$el.parent().height();

        return maxHeight || parentHeight || 300;
    },

    getChildren() {
        return this.list.children;
    },

    renderListView() {
        this.listHeight = this.getListHeight();

        // start off with just the models that need to render. Others get added/removed when scrolling.
        const collection = new Backbone.Collection(_.take(this.collection.filter(this.getFilter()), this.getViewsCount() + 6));
        this.list = new ListView({
            collection,
            _parent: this,
            childViewTriggers: this.getOption("childViewTriggers") // For some reason we can't do this in list constructor...
        });

        this.showChildView("scrollable", this.list);
    },

    onAttach() {
        this.renderListView();
        // Create a boolean array to track view visibility
        this._allViews = Array.from({
            length: this.collection.filter(this.getFilter()).length
        }).fill(false);
    
        const innerListHeight = this._allViews.length * this.getOption("itemHeight");
    
        // Add height to main container
        this.el.style.height = `${innerListHeight}px`;
    
        // Add height to virtualized list container to enable scrollbar
        this.list.el.style.height = `${innerListHeight}px`;
    
        this.list.children.forEach((child, index) => {
            this._allViews[index] = true;
            child.el.style.top = `${index * this.getOption("itemHeight")}px`;
            child.el.dataset.virt_index = index;
        });
    },

    getFilter() {
        return this.filter || _.constant(true);
    },

    setFilter(fn) {
        this.filter = fn;
        this.onAttach();
    },

    removeFilter() {
        if (this.filter) {
            delete this.filter;
            this.onAttach();
        }
    }
});
